import { Cancel } from '#base/Canvas/Cancel.js';
import { DesignGridWrapper } from '#base/Designer/index.js';
import { GroupToolView } from '#base/Designer/GroupToolView.js';
import { ToolView } from '#base/Designer/ToolView.js';
import { ToolKeys } from '#base/Designer/types.js';
import GroupToolIcon from '#base/resources/icons/group/text-tool.svg';
import { CleanTextArea, Font, MultiText } from '#base/Text/Components.js';
import { DEFAULT_COLOR, isTextClean, NAME_PLACEHOLDER, TEXT_PLACEHOLDER } from '@c2s-npm/plumlib';
import React, { useEffect, useState } from 'react';

export const FontSelect = ({
  fonts: assets,
  groups,
  onSelect,
  selected,
  cancel,
  revert,
  elements,
  hasPremium,
  graphicType,
  defaultColor,
}) => {
  const [align, setAlign] = useState(selected?.get?.('align') || 'center');

  const handleChange = (asset, { data, align, color }) => {
    onSelect({
      elementType: 'font',
      assetId: asset.key,
      fontName: asset.key,
      data,
      align,
      color,
    });
  };

  return (
    <ToolView
      {...{
        toolKey: 'text',
        assets,
        groups,
        onSelect: handleChange,
        selected,
        cancel,
        revert,
        elements,
        hasPremium,
        renderInput: FontInput,
        renderAssets: FontAssets,
        placeholder: TEXT_PLACEHOLDER,
        graphicType,
        defaultColor,
        align,
        setAlign,
      }}
    />
  );
};

const FontInput = ({
  selected,
  setInput,
  commit,
  undo,
  canRevert,
  isNew,
  placeholder,
  defaultColor,
  align,
}) => {
  const [text, setText] = useState(selected?.get?.('data') || '');
  const color = selected?.get?.('color') || defaultColor || DEFAULT_COLOR;

  const handleChange = (e) => {
    const {
      target: { value },
    } = e;
    setText(value);
  };

  useEffect(() => {
    setInput({
      data: (isTextClean(text) && text) || placeholder,
      align,
      color,
    });
  }, [text, align, color]);

  return (
    <>
      <CleanTextArea value={text} placeholder={placeholder} handleChange={handleChange} />

      <div className='t-mt-5 t-text-center sm:t-relative sm:t-w-auto sm:t-bg-transparent'>
        {/* <div className='t-fixed t-bottom-0 t-left-0 t-right-0 t-z-30 t-w-screen t-bg-white t-px-4 t-text-center t-shadow-[0px_-3px_15px_rgba(0,0,0,0.2)] sm:t-relative sm:t-mt-5 sm:t-w-auto sm:t-bg-transparent sm:t-shadow-none'> */}
        <Cancel
          disabled={!text || !isTextClean(text)}
          {...{ commit, undo, canRevert, isNew, label: 'Text' }}
        />
      </div>
    </>
  );
};

const getTitle = (view) => {
  if (view === 'input') {
    return 'Enter Names';
  } else {
    return 'Name';
  }
};

const defaultData = [{ data: '', isDefault: true }];
export const FontGroupSelect = ({
  fonts: assets,
  groups,
  onSelect,
  selected,
  cancel,
  revert,
  elements,
  hasPremium,
  graphicType,
  defaultColor,
}) => {
  const handleChange = (asset, { data, align, color }) => {
    onSelect({
      isGroup: true,
      elementType: 'font',
      assetId: asset.key,
      fontName: asset.key,
      align,
      color,
      data,
    });
  };

  return (
    <GroupToolView
      {...{
        toolKey: ToolKeys.GROUP_NAME,
        image: GroupToolIcon,
        getTitle,
        assets,
        groups,
        onSelect: handleChange,
        selected,
        cancel,
        revert,
        elements,
        hasPremium,
        graphicType,
        defaultColor,
        renderInput: FontGroupInput,
        renderAssets: FontAssets,
        placeholder: NAME_PLACEHOLDER,
      }}
    />
  );
};

const FontGroupInput = ({
  selected,
  input,
  setInput,
  commit,
  undo,
  canRevert,
  isNew,
  placeholder,
  graphicType,
  defaultColor,
}) => {
  const [inputData, setData] = useState(selected?.get?.('data')?.toJS?.() || input?.data || defaultData);

  const handleChange = (i, v) => {
    const newData = [...inputData];
    newData.forEach((d) => delete d.active);
    newData[i] = Object.assign({ active: true }, newData[i], v);
    !!v && delete newData[i].isDefault;
    setData(newData);
  };

  const handleDelete = (i) => {
    const newData = [...inputData];
    newData.splice(i, 1);
    setData(newData);
  };

  const handleAdd = (n = 1) => {
    const newData = [
      ...inputData,
      ...(Array.isArray(n)
        ? n
        : Array.apply(null, Array(n)).map(() => ({ data: '', active: true, isDefault: true }))),
    ];
    newData.forEach((d) => delete d.active);
    newData[newData.length - 1].active = true;
    setData(newData);
  };

  useEffect(() => {
    setInput({
      data: inputData.map(({ data: text, ...d }) =>
        Object.assign(d, { data: (isTextClean(text) && text) || placeholder })
      ),
      align: selected?.get?.('align') || 'center',
      color: graphicType === 'engraved' ? null : selected?.get?.('color') || defaultColor || DEFAULT_COLOR,
    });
  }, [inputData]);

  const inputText = inputData.find((d) => !!d.active)?.data || inputData[0]?.data || placeholder;

  return (
    <>
      <MultiText
        data={inputData}
        onChange={handleChange}
        onDelete={handleDelete}
        onAdd={handleAdd}
        placeholder={placeholder}
        csvLabel='Name'
        uploadLabel='Add Names'
        toolTitle='Name'
      />

      <div className='t-mt-5 t-text-center sm:t-relative sm:t-w-auto sm:t-bg-transparent'>
        <Cancel
          disabled={!inputText || !isTextClean(inputText)}
          {...{ commit, undo, canRevert, isNew, label: 'Names' }}
        />
      </div>
    </>
  );
};

const FontAssets = ({ input, assets, selectedAsset, setAsset, hasPremium, placeholder }) => {
  const { data, align } = input;
  const text = Array.isArray(data) ? (data.find((d) => !!d.active) || data[0]).data : data;

  return (
    <>
      <div className='t-mx-auto t-max-w-[360px] lg:t-max-w-full'>
        <DesignGridWrapper className='t-overflow-x-hidden t-px-3 t-pb-5' mode='grid'>
          {assets.map((asset, i) => (
            <Font
              key={i}
              font={asset}
              text={!!text && isTextClean(text) ? text : placeholder}
              selected={selectedAsset === asset}
              onClick={setAsset}
              {...{ align, hasPremium }}
            />
          ))}
        </DesignGridWrapper>
      </div>
    </>
  );
};
